import { useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { useDeckStore } from '../store/deckStore';
import { BrandLockup } from './BrandMark';
import { DeckLibrary } from './DeckLibrary';
import { ShareDialog } from './ShareDialog';
import { VersionHistoryDialog } from './VersionHistoryDialog';
import { navigate, paths } from '../lib/router';
import { UserMenu } from './UserMenu';
import { NotificationsBell } from './NotificationsBell';
import { useAuthStore } from '../store/authStore';

interface Props {
  onMagicDraft: () => void;
}

function SaveStatus() {
  const saveStatus = useDeckStore((s) => s.saveStatus);
  const [icon, label, color] =
    saveStatus === 'saving'
      ? [<CircularProgress key="i" size={12} color="inherit" />, 'Saving…', 'text.secondary']
      : saveStatus === 'saved'
        ? [<CheckIcon key="i" sx={{ fontSize: 15 }} />, 'Saved', 'success.main']
        : saveStatus === 'offline'
          ? [<CloudOffOutlinedIcon key="i" sx={{ fontSize: 15 }} />, 'Saved locally', 'text.secondary']
          : saveStatus === 'conflict'
            ? [<ErrorOutlineIcon key="i" sx={{ fontSize: 15 }} />, 'Conflict', 'error.main']
            : saveStatus === 'error'
              ? [<ErrorOutlineIcon key="i" sx={{ fontSize: 15 }} />, 'Save failed', 'error.main']
              : [null, '', 'text.secondary'];
  if (!label) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', color }}>{icon}</Box>
      <Typography variant="caption">{label}</Typography>
    </Stack>
  );
}

function shareIcon(visibility: string) {
  if (visibility === 'company') return <PeopleOutlineIcon color="primary" />;
  if (visibility === 'unlisted') return <LinkOutlinedIcon color="primary" />;
  return <LockOutlinedIcon />;
}

function shareLabel(visibility: string) {
  if (visibility === 'company') return 'Shared';
  if (visibility === 'unlisted') return 'Link shared';
  return 'Share';
}

export function Navbar({ onMagicDraft }: Props) {
  const title = useDeckStore((s) => s.title);
  const setTitle = useDeckStore((s) => s.setTitle);
  const undo = useDeckStore((s) => s.undo);
  const redo = useDeckStore((s) => s.redo);
  const canUndo = useDeckStore((s) => s.past.length > 0);
  const canRedo = useDeckStore((s) => s.future.length > 0);
  const setPresenting = useDeckStore((s) => s.setPresenting);
  const slides = useDeckStore((s) => s.slides);
  const syncSave = useDeckStore((s) => s.syncSave);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const aiEnabled = useAuthStore((s) => s.aiEnabled);
  const offline = useAuthStore((s) => s.status === 'offline');
  const visibility = useDeckStore((s) => s.visibility);
  const saveStatus = useDeckStore((s) => s.saveStatus);
  const syncSaveStore = useDeckStore((s) => s.syncSave);
  const reloadFromServer = useDeckStore((s) => s.reloadFromServer);
  const deckId = useDeckStore((s) => s.deckId);
  const [shareOpen, setShareOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const onExport = async (kind: 'pptx' | 'pdf') => {
    setExportAnchor(null);
    setExporting(true);
    try {
      await syncSave();
      if (kind === 'pptx') {
        const { exportPptx } = await import('../lib/exportPptx');
        await exportPptx({ title, slides });
      } else {
        const { exportPdf } = await import('../lib/exportPdf');
        await exportPdf({ title });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppBar>
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 56 }, px: { xs: 2 } }}>
        {offline ? (
          <BrandLockup />
        ) : (
          <Tooltip title="Home">
            <ButtonBase
              onClick={() => navigate(paths.home())}
              aria-label="Slide home"
              sx={{ borderRadius: 1, p: 0.5, m: -0.5 }}
            >
              <BrandLockup />
            </ButtonBase>
          </Tooltip>
        )}
        <Divider orientation="vertical" flexItem sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
          {!offline && (
            <Tooltip title="Your decks">
              <IconButton aria-label="Your decks" onClick={() => setLibraryOpen(true)}>
                <FolderOpenOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <InputBase
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ input: { 'aria-label': 'Deck title' } }}
            sx={{
              width: 280,
              minWidth: 0,
              px: 1,
              py: 0.25,
              borderRadius: 1.5,
              typography: 'subtitle2',
              border: 1,
              borderColor: 'transparent',
              '&:hover': { borderColor: 'divider' },
              '&.Mui-focused': { borderColor: 'primary.main' },
            }}
          />
          <SaveStatus />
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Tooltip title="Undo">
            <span>
              <IconButton onClick={undo} disabled={!canUndo} aria-label="Undo">
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo">
            <span>
              <IconButton onClick={redo} disabled={!canRedo} aria-label="Redo">
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />

          {aiEnabled && (
            <Button
              color="neutral"
              startIcon={<AutoAwesomeOutlinedIcon color="primary" />}
              onClick={onMagicDraft}
            >
              Draft with AI
            </Button>
          )}
          {!offline && deckId && (
            <Tooltip title="Version history">
              <IconButton aria-label="Version history" onClick={() => setHistoryOpen(true)}>
                <HistoryOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!offline && (
            <Button color="neutral" startIcon={shareIcon(visibility)} onClick={() => setShareOpen(true)}>
              {shareLabel(visibility)}
            </Button>
          )}
          <Button
            variant="outlined"
            color="neutral"
            startIcon={<PlayArrowOutlinedIcon />}
            onClick={() => setPresenting(true)}
          >
            Present
          </Button>
          <Button
            variant="contained"
            disabled={exporting}
            startIcon={
              exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />
            }
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 200 } } }}
          >
            <MenuItem onClick={() => void onExport('pptx')}>
              <ListItemText primary="PowerPoint" secondary=".pptx, editable" />
            </MenuItem>
            <MenuItem onClick={() => void onExport('pdf')}>
              <ListItemText primary="PDF" secondary=".pdf, for sharing" />
            </MenuItem>
          </Menu>
          {!offline && <NotificationsBell />}
          <UserMenu />
        </Stack>
      </Toolbar>

      <DeckLibrary open={libraryOpen} onClose={() => setLibraryOpen(false)} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
      <VersionHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={saveStatus === 'conflict'}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => void reloadFromServer()}>
                Reload
              </Button>
              <Button color="inherit" size="small" onClick={() => void syncSaveStore({ force: true })}>
                Overwrite
              </Button>
            </Stack>
          }
        >
          This deck was changed elsewhere. Reload to keep their edits, or overwrite with yours.
        </Alert>
      </Snackbar>
    </AppBar>
  );
}
